import { Select, SelectOption, Spinner, Typography } from '@SBGSports/referee-react';
import { RadioButton } from '../../reporting/components/radio-button/radio-button';
import { SyncToFocusTable } from '../component/focus-table/sync-to-focus-table';
import { useMemo, useEffect, useState, useRef } from 'react';
import { useFocusImport } from '../use-focus-import';
import { useGetDeepActivity } from 'api';
import styles from '../focus-import.module.css';
import { useTranslations } from 'i18n';
import { LEARN_MORE_LINK, TAG_OPTION, TIMING_OPTION } from '../constants';
import { useFocusStoredOptions } from '../use-focus-stored-options';
import { FocusTag, FocusTimingTag } from 'shared/file-importer/types';
import { PageAttributeProps } from 'shared/modals/multi-page-modal/types';
import { useMultiPageWizard } from 'shared/modals/multi-page-modal/use-multi-page-wizard';
import { defaultNavigationBtns } from 'shared/modals/multi-page-modal/multi-page-modal';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from 'analytics/analytics-events';
import { useFocusImportSelections } from '../use-focus-import-selections';

const getTagValues = (tags: FocusTag[] | undefined, selection: string | undefined): SelectOption[] => {
    if (tags && selection) {
        return tags
            ?.filter((t) => t.tagName === selection)
            .map((t) => ({ value: new Date(t.tagStart).toISOString().substring(11, 19), id: t.tagId }));
    }

    return [];
};

const getTimingValues = (timing: FocusTimingTag[] | undefined): SelectOption[] => {
    return timing?.map((t) => ({ value: t.period_id, id: t.period_id })) ?? [];
};

export const AlignPeriodsScreen: React.FC<PageAttributeProps> = (props) => {
    const { setButtonDetails } = props;

    // Hooks
    const { activityId, focusFile } = useFocusImport();
    const { setHeader } = useMultiPageWizard();
    const { data, isLoading } = useGetDeepActivity(activityId);
    const { __ } = useTranslations();
    const { focusStoredOptions, setFocusStoredOptions } = useFocusStoredOptions();
    const tagTypeSelectOptions: SelectOption[] = useMemo(() => {
        return [...new Set(focusFile?.tags.map((t) => t.tagName))].map((tagName) => ({
            id: tagName,
            value: tagName,
        }));
    }, [focusFile?.tags]);
    const { setAlignPeriodOption, setAlignPeriodMap } = useFocusImportSelections();

    // State
    const [tableDropdownOptions, setTableDropdownOptions] = useState<SelectOption[]>(
        focusStoredOptions.alignPeriodOption === TIMING_OPTION
            ? getTimingValues(focusFile?.timing)
            : getTagValues(focusFile?.tags, focusStoredOptions.alignPeriodTagTypeSelection),
    );
    const [localSelectionState, setLocalSelectionState] = useState<Map<string, string>>(() => {
        const initialSelection = new Map<string, string>();
        const periods = data?.[0]?.periods ?? [];
        periods.forEach((period) => {
            const periodId = period.id ?? '';
            const alignment = focusStoredOptions.syncToFocusMap.get(periodId);
            if (alignment && tableDropdownOptions.find((option) => option.id === alignment)) {
                initialSelection.set(periodId, alignment ?? '');
            }
        });
        return initialSelection;
    });

    /**
     * Set header and custom button details here on first render to display on parent component
     */
    const executedRef = useRef(false);
    useEffect(() => {
        if (executedRef.current) {
            return;
        }
        mixpanel.track(EVENTS.focus.alignPeriodStarted, {});
        mixpanel.time_event(EVENTS.focus.alignPeriodEnded);
        setButtonDetails?.(defaultNavigationBtns(__, localSelectionState.size === 0));
        setHeader(
            <Typography variant="heading-2">
                {__('smt_importer.align_import_title', { productName: 'Focus' })}
            </Typography>,
        );
        executedRef.current = true;
        return () => {
            mixpanel.track(EVENTS.focus.alignPeriodEnded);
        };
    }, [__, setHeader, setButtonDetails, localSelectionState]);

    /**
     *  Get user selection of for this screen
     */
    useEffect(() => {
        setAlignPeriodOption(focusStoredOptions?.alignPeriodOption ?? TIMING_OPTION);
    }, [focusStoredOptions?.alignPeriodOption, setAlignPeriodOption]);

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <div className={styles['start-page-body']}>
            <Typography variant="body-1">
                {__('smt_importer.align_import_description').concat(' ')}
                <a target="window" href={LEARN_MORE_LINK} className={styles['learn_more_link']}>
                    {__('misc.learn_more')}
                </a>
            </Typography>
            <div>
                <RadioButton
                    label={__('smt_importer.timing_tag')}
                    onChange={(option) => {
                        if (option !== focusStoredOptions.alignPeriodOption) {
                            setTableDropdownOptions(getTimingValues(focusFile?.timing));
                            setFocusStoredOptions({
                                ...focusStoredOptions,
                                syncToFocusMap: new Map(),
                                alignPeriodOption: option as typeof TIMING_OPTION,
                            });
                            setButtonDetails?.(defaultNavigationBtns(__, true));
                            mixpanel.track(EVENTS.focus.alignPeriodOptionChanged, { optionSelected: TIMING_OPTION });
                        }
                    }}
                    value={TIMING_OPTION}
                    isChecked={focusStoredOptions.alignPeriodOption === TIMING_OPTION}
                    name={TIMING_OPTION}
                    disabled={focusFile?.timing?.length === 0 ?? true}
                />
                <div className={styles['align-periods-radio-select']}>
                    <RadioButton
                        label={__('smt_importer.focus_tag')}
                        onChange={(option) => {
                            if (option !== focusStoredOptions.alignPeriodOption) {
                                const values = getTagValues(
                                    focusFile?.tags,
                                    focusStoredOptions.alignPeriodTagTypeSelection,
                                );
                                if (values) {
                                    setTableDropdownOptions(values);
                                }
                                setFocusStoredOptions({
                                    ...focusStoredOptions,
                                    syncToFocusMap: new Map(),
                                    alignPeriodOption: option as typeof TAG_OPTION,
                                });
                                setButtonDetails?.(defaultNavigationBtns(__, true));
                                mixpanel.track(EVENTS.focus.alignPeriodOptionChanged, { optionSelected: TAG_OPTION });
                            }
                        }}
                        value={TAG_OPTION}
                        isChecked={focusStoredOptions.alignPeriodOption === TAG_OPTION}
                        name={TAG_OPTION}
                        disabled={false}
                        description={__('smt_importer.focus_tag_prompt')}
                        inline
                    />
                    <Select
                        testId={'align-periods-tag-type-select'}
                        menuItems={tagTypeSelectOptions}
                        defaultOption={
                            focusStoredOptions.alignPeriodOption === TAG_OPTION
                                ? tagTypeSelectOptions?.find(
                                      (option) => option.value === focusStoredOptions.alignPeriodTagTypeSelection,
                                  )
                                : undefined
                        }
                        isSearchable
                        onSelectItem={(selectedItem) => {
                            const selection = String(selectedItem?.[0]?.value);
                            const options = getTagValues(focusFile?.tags, selection);
                            if (options) {
                                setTableDropdownOptions(options);
                            }
                            setFocusStoredOptions({
                                ...focusStoredOptions,
                                syncToFocusMap: new Map(),
                                alignPeriodOption: TAG_OPTION,
                                alignPeriodTagTypeSelection: selection,
                            });
                            setButtonDetails?.(defaultNavigationBtns(__, true));
                            mixpanel.track(EVENTS.focus.alignPeriodOptionChanged, {
                                optionSelected: TAG_OPTION,
                                tagTypeSelected: selection,
                            });
                        }}
                        size={'small'}
                        style={{ width: '216px' }}
                        disabled={focusStoredOptions.alignPeriodOption !== TAG_OPTION}
                    />
                </div>
            </div>
            <SyncToFocusTable
                periods={data?.[0]?.periods ?? []}
                syncToFocusOptions={tableDropdownOptions}
                selectOptionsMap={focusStoredOptions.syncToFocusMap ?? new Map()}
                setSelectOptionsMap={(map) => {
                    setAlignPeriodMap(map);
                    setLocalSelectionState(map);
                    setFocusStoredOptions({ ...focusStoredOptions, syncToFocusMap: map });
                    mixpanel.track(EVENTS.focus.alignPeriod, { syncToFocusMap: map });
                    setButtonDetails?.(defaultNavigationBtns(__, map.size === 0));
                }}
            />
        </div>
    );
};
